<template>
  <div id="container">
    <h1>{{ $t('projectsList.addProject') }}</h1>
    <p class="required-fields-info">
      {{ $t('projectForm.mandatoryFieldsInfo') }}
    </p>
    <project-form
      :project-type="ProjectType.SUPPLIER_PROJECT"
      @submit="handleSubmit"
    >
      <docu-managers-table
        :mode="DocuManagersTableMode.NEW_PROJECT"
        @update:selected-docu-managers="handleDocuManagersUpdated"
      />
      <message-wrapper
        v-if="isMissingDocuManagersError"
        class="no-docu-managers-error"
        content-centred
        severity="error"
      >
        {{ $t('createProject.errors.noDocuManagersAdded') }}
      </message-wrapper>
    </project-form>
  </div>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '../../composables/useToast';
  import ProjectForm from '../../components/ProjectForm/ProjectForm.vue';
  import { IDocuManager, ProjectErrorCode, ProjectType } from 'types';
  import { ProjectFormFields } from '@/components/ProjectForm/ProjectForm.types';
  import { DocuManagersTableMode } from '@/components/DocuManagersTable/DocuManagersTable.types';
  import { ProjectDataService } from '@/services';
  import { Route, routeParams } from '@/routes';
  import { computed } from 'vue';
  import { isCustomErrorCodeException } from '@/helpers';

  const { t } = useI18n();
  const router = useRouter();
  const { createErrorToast } = useToast();

  const addedDocuManagers = ref<string[]>([]);
  const isSubmitted = ref<boolean>(false);

  const isMissingDocuManagersError = computed(
    () => isSubmitted.value && !addedDocuManagers.value.length,
  );

  const handleSubmit = async (projectData: ProjectFormFields) => {
    isSubmitted.value = true;
    if (!addedDocuManagers.value.length) return;

    try {
      const project = await ProjectDataService.createProject(
        projectData,
        addedDocuManagers.value,
      );
      router.push({
        name: Route.PROJECT_DETAILS,
        params: {
          [routeParams[Route.PROJECT_DETAILS].projectId]:
            project._id.toString(),
        },
      });
    } catch (err) {
      if (isCustomErrorCodeException(err)) {
        if (
          err.response?.data.customErrorCode ===
          ProjectErrorCode.DUPLICATE_PROJECT_NUMBER
        ) {
          createErrorToast(
            t('projectForm.errors.projectNumberNotUniqueForSite'),
          );
        } else if (
          err.response?.data.customErrorCode ===
          ProjectErrorCode.DUPLICATE_EXTERNAL_PROJECT_ID
        ) {
          createErrorToast(t('projectForm.errors.externalIdNotUnique'));
        }
      } else createErrorToast(t('createProject.errors.couldNotCreateProject'));
    }
  };

  const handleDocuManagersUpdated = (docuManagers: IDocuManager[]): void => {
    addedDocuManagers.value = docuManagers.map(
      (docuManager) => docuManager.oid,
    );
  };
</script>

<style lang="scss" scoped>
  #container {
    padding-right: 20px;
    h1 {
      font-weight: normal;
      font-size: 2.25rem;
      margin-bottom: 20px;
    }
    .required-fields-info {
      margin-bottom: 1rem;
    }
    .no-docu-managers-error {
      margin-top: 1rem;
      text-align: center;
    }
  }
</style>
